import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Printer, Download } from "lucide-react";
import { fmtFCFA, fmtDate } from "../lib/format";
import { useBrand } from "./Shared";
import { downloadExport } from "../lib/api";
import { toast } from "sonner";

/**
 * Reusable printable receipt / voucher for income & payment transactions.
 * type: "income" | "payment"
 */
export default function ReceiptModal({ open, onClose, type, data }) {
  const brand = useBrand();
  if (!data) return null;
  const isIncome = type === "income";
  const receiptNo = (data.id || "").slice(0, 8).toUpperCase();

  const downloadPdf = async () => {
    const path = isIncome ? `/export/income/${data.id}/receipt.pdf` : `/export/payment/${data.id}/voucher.pdf`;
    const fname = `${isIncome ? "income_receipt" : "payment_voucher"}_${receiptNo}.pdf`;
    const t = toast.loading("Preparing PDF…");
    try { await downloadExport(path, fname); toast.success("PDF downloaded", { id: t }); }
    catch { toast.error("Download failed", { id: t }); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogTitle className="sr-only">{isIncome ? "Income Receipt" : "Payment Voucher"}</DialogTitle>
        <DialogDescription className="sr-only">Printable {isIncome ? "income receipt" : "payment voucher"} for this transaction.</DialogDescription>
        <div className="print-area bg-white">
          <div className="bg-[#0B132B] text-white px-6 py-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading text-lg font-extrabold">{brand.name}</div>
                <div className="text-[11px] text-sky-300 tracking-wide">Cameroon · Official Voucher</div>
              </div>
              {brand.logo_url ? (
                <img src={brand.logo_url} alt={brand.name} className="h-10 w-10 rounded-lg object-cover bg-white/10" />
              ) : (
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-sky-400 to-blue-700 flex items-center justify-center font-heading font-extrabold text-sm">ST</div>
              )}
            </div>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">
                  {isIncome ? "Income Receipt" : "Payment Voucher"}
                </div>
                <div className="font-heading font-bold text-slate-900 text-lg">
                  {isIncome ? "INCOME RECEIPT" : "PAYMENT VOUCHER"}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-400">Receipt No.</div>
                <div className="font-mono-num font-bold text-slate-900">#{receiptNo}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-y-3 text-sm">
              <Row label="Date" value={fmtDate(data.date || data.created_at)} />
              <Row label="Project" value={data.project_name || "—"} />
              {isIncome && data.source && <Row label="Source" value={data.source} />}
              {!isIncome && <Row label="Purpose" value={data.purpose + (data.purpose_detail ? ` (${data.purpose_detail})` : "")} />}
              {!isIncome && <Row label="Employee" value={data.employee_name || "—"} />}
              <Row label="Method" value={data.payment_method || "—"} />
              {data.reference && <Row label="Reference" value={data.reference} />}
              {isIncome && data.external_ref && <Row label="External Ref" value={data.external_ref} />}
              {!isIncome && data.check_number && <Row label="Cheque No." value={data.check_number} />}
              <Row label="Recorded by" value={data.created_by_name || "—"} />
            </div>

            <div className="mt-5 bg-slate-50 border border-slate-200 rounded-lg px-4 py-4 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-500">Amount</span>
              <span className="font-mono-num text-2xl font-extrabold text-slate-900">
                {fmtFCFA(data.amount)}
              </span>
            </div>

            {(data.remarks) && (
              <div className="mt-4 text-sm text-slate-600">
                <span className="text-slate-400">Remarks: </span>{data.remarks}
              </div>
            )}

            <div className="mt-10 flex items-end justify-between">
              <div className="text-center">
                <div className="w-40 border-t border-slate-400 pt-1 text-xs text-slate-500">
                  Authorized Signature
                </div>
              </div>
              <div className="text-[10px] text-slate-400 text-right">
                Generated by STEMS<br />SilasTech Enterprise Management System
              </div>
            </div>
          </div>
        </div>

        <div className="no-print flex justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={onClose} data-testid="receipt-close-button">Close</Button>
          <Button variant="outline" onClick={downloadPdf} data-testid="receipt-download-button">
            <Download className="h-4 w-4 mr-2" /> Download PDF
          </Button>
          <Button onClick={() => window.print()} data-testid="receipt-print-button" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">
            <Printer className="h-4 w-4 mr-2" /> Print Receipt
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="col-span-1">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="font-medium text-slate-800">{value}</div>
    </div>
  );
}
